import {
    ApplicationConfig,
    inject,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localePl from '@angular/common/locales/pl';
import localeNl from '@angular/common/locales/nl';
import localeTr from '@angular/common/locales/tr';
import localeCs from '@angular/common/locales/cs';
import localeRo from '@angular/common/locales/ro';
import localeIt from '@angular/common/locales/it';
import localeRu from '@angular/common/locales/ru';
import localeDa from '@angular/common/locales/da';
import localeSv from '@angular/common/locales/sv';
import localePt from '@angular/common/locales/pt';
import localeEl from '@angular/common/locales/el';
import localeHu from '@angular/common/locales/hu';
import localeSk from '@angular/common/locales/sk';
import localeSl from '@angular/common/locales/sl';
import localeHr from '@angular/common/locales/hr';
import localeBg from '@angular/common/locales/bg';
import localeEt from '@angular/common/locales/et';
import localeFi from '@angular/common/locales/fi';
import localeGa from '@angular/common/locales/ga';
import localeLt from '@angular/common/locales/lt';
import localeLv from '@angular/common/locales/lv';
import localeMt from '@angular/common/locales/mt';
import localeNb from '@angular/common/locales/nb';

import { routes } from './app.routes';
import { csrfInterceptor } from './services/csrf.interceptor';
import { GrowthbookService } from "./services/growthbook.service";
import { LanguageService } from './services/language.service';
import { ENV } from './environment';

// Register locale data for supported languages
registerLocaleData(localeEs, 'es');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localeTr, 'tr');
registerLocaleData(localeCs, 'cs');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeRo, 'ro');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeRu, 'ru');
registerLocaleData(localeDa, 'da');
registerLocaleData(localeSv, 'sv');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeEl, 'el');
registerLocaleData(localeHu, 'hu');
registerLocaleData(localeSk, 'sk');
registerLocaleData(localeSl, 'sl');
registerLocaleData(localeHr, 'hr');
registerLocaleData(localeBg, 'bg');
registerLocaleData(localeEt, 'et');
registerLocaleData(localeFi, 'fi');
registerLocaleData(localeGa, 'ga');
registerLocaleData(localeLt, 'lt');
registerLocaleData(localeLv, 'lv');
registerLocaleData(localeMt, 'mt');
registerLocaleData(localeNb, 'nb');

function initGrowthbook() {
    return () => {
        const gb = inject(GrowthbookService);
        return gb.init(2000); // wait up to 2s for flags; or skip await if flicker is acceptable
    };
}

function initLocaleRedirect() {
    return () => {
        const langService = inject(LanguageService);

        // Only attempt hard redirect when building localized bundles (production).
        if (!ENV.production) {
            return;
        }

        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            return;
        }

        const supported = langService.supportedLanguages.map(l => l.code);

        const path = window.location.pathname || '/';
        const hasLocalePrefix = supported.some(code => path === `/${code}` || path.startsWith(`/${code}/`));

        if (hasLocalePrefix) {
            return; // already on a locale-specific URL
        }

        const stored = langService.getStoredLanguagePreference();
        const detected = langService.detectBrowserLanguage();
        const target = (stored && supported.includes(stored)) ? stored : detected;

        // Default English is served at root; avoid redirect if en-US
        if (target && target !== 'en-US' && supported.includes(target)) {
            const newUrl = `/${target}${path}${window.location.search}${window.location.hash}`;
            // Use replace to avoid polluting history
            window.location.replace(newUrl);
        }
    };
}

function localeIdFactory(): string {
    try {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return 'en-US';
        }
        const supported = ['en-US','es','fr','de','nl','tr','cs','pl','ro','it','ru','da','sv','pt','el','hu','sk','sl','hr','bg','et','fi','ga','lt','lv','mt','nb'];
        const path = window.location.pathname || '/';
        const firstSeg = path.split('/')[1];
        if (supported.includes(firstSeg)) {
            return firstSeg;
        }
        const htmlLang = (document.documentElement.getAttribute('lang') || '').trim();
        if (supported.includes(htmlLang)) {
            return htmlLang;
        }
        if (htmlLang.startsWith('en')) {
            return 'en-US';
        }
    } catch {}
    return 'en-US';
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes, withInMemoryScrolling({
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled'
        })),
        provideHttpClient(withInterceptors([csrfInterceptor])),
        // { provide: LOCALE_ID, useFactory: localeIdFactory },
        provideAppInitializer(initGrowthbook()),
        // provideAppInitializer(initLocaleRedirect()),
    ]
};
